import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { theme } from '../../../styles/theme';
import Page from '../../../common/Page';
import Loader from '../../../common/Loader';
import EmptyState from '../../../common/EmptyState';
import DeleteEventButton from '../../../common/DeleteEventButton';
import PrivateEventFields, { privateFieldsFromEvent, fieldStyles } from '../../../common/PrivateEventFields';
import { getEventInfo, updatePrivateEvent } from '../../../common/api';
import { formatEventDateTime, toDateTimeLocal } from '../../../common/dates';
import { displayEventName, safeBackTarget } from '../../../common/events';
import { useCurrentUser, isSuperAdmin } from '../../../common/UserContext';
import { whatsappLink } from '../../../common/phone';

// Scheda di un evento privato: nessun biglietto su HubSpot, solo informazioni e note. I super-admin possono modificarla.
function PrivateEvent() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const eventKey = queryParams.get('eventKey') || '';
  const backTarget = safeBackTarget(queryParams.get('back'));
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canEdit = isSuperAdmin(user);

  const [isLoading, setIsLoading] = useState(true);
  const [eventInfo, setEventInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const loadedKey = useRef(null); // evita di ricaricare quando l'URL cambia dopo un salvataggio che ha modificato la chiave

  useEffect(() => {
    if (!eventKey) {
      setIsLoading(false);
      return;
    }
    if (loadedKey.current === eventKey) {
      return;
    }
    setIsLoading(true);
    getEventInfo(eventKey)
      .then(({ event }) => { loadedKey.current = event.eventKey; setEventInfo(event); })
      .catch((error) => {
        console.error('Error fetching event:', error);
        toast.error(error.status === 404 ? 'Evento non trovato' : "Errore nel caricamento dell'evento");
      })
      .finally(() => setIsLoading(false));
  }, [eventKey]);

  const startEditing = () => {
    setForm({
      when: toDateTimeLocal(eventInfo.when),
      notes: eventInfo.notes || '',
      ...privateFieldsFromEvent(eventInfo),
    });
    setErrors({});
    setEditing(true);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.when) {
      setErrors({ when: 'Data e ora sono obbligatorie' });
      return;
    }
    setSaving(true);
    try {
      const { event } = await updatePrivateEvent({ eventKey, ...form });
      toast.success('Evento aggiornato');
      setEventInfo(event);
      setEditing(false);
      if (event.eventKey !== eventKey) {
        // la data fa parte della chiave: aggiorna l'URL senza aggiungere una voce di cronologia
        loadedKey.current = event.eventKey;
        const params = new URLSearchParams(location.search);
        params.set('eventKey', event.eventKey);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      }
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(error.status === 409 ? 'Esiste già un evento con questo nome, data e ora' : (error.message || "Errore nel salvataggio dell'evento"));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  const rows = eventInfo
    ? [
        { label: 'Quando', value: `${formatEventDateTime(eventInfo.when)}${eventInfo.duration ? ` (durata ${eventInfo.duration})` : ''}` },
        {
          label: 'Referente',
          value: [eventInfo.contactName, eventInfo.contactPhone].filter(Boolean).join(' · '),
          render: (eventInfo.contactName || eventInfo.contactPhone) && (
            <>
              {eventInfo.contactName}
              {eventInfo.contactName && eventInfo.contactPhone ? ' · ' : ''}
              {eventInfo.contactPhone && (
                <a
                  href={whatsappLink(eventInfo.contactPhone) || `tel:${eventInfo.contactPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.whatsapp}
                  title="Apri la chat WhatsApp"
                >
                  {eventInfo.contactPhone}
                </a>
              )}
            </>
          ),
        },
        { label: 'Dove', value: eventInfo.where },
        { label: 'N° persone', value: eventInfo.participants === null || eventInfo.participants === undefined ? '' : `${eventInfo.participants} persone` },
        { label: 'Ceramiche', value: eventInfo.ceramics },
        { label: 'Costo', value: eventInfo.cost },
        { label: 'Metodo di pagamento', value: eventInfo.paymentMethod },
        { label: 'Informazioni', value: eventInfo.informations },
      ]
    : [];

  return (
    <Page>
      <div style={styles.content}>
        <div style={styles.topRow}>
          <button onClick={() => navigate(backTarget)} style={styles.backArrow} aria-label="Indietro">
            ←
          </button>
          <span style={styles.typeBadge}>Evento privato</span>
        </div>

        {!eventInfo ? (
          <EmptyState text="Evento non trovato" />
        ) : editing ? (
          <form onSubmit={handleSave} style={styles.form} autoComplete="off" noValidate>
            <h4 style={styles.eventTitle}>{displayEventName(eventInfo.eventName)}</h4>
            <label style={fieldStyles.label}>
              Quando (data e ora di inizio) *
              <input type="datetime-local" value={form.when} onChange={(e) => setField('when', e.target.value)} style={fieldStyles.input} />
              {errors.when && <span style={fieldStyles.error}>{errors.when}</span>}
            </label>
            <PrivateEventFields values={form} onChange={setField} />
            <label style={fieldStyles.label}>
              Note
              <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={4} style={fieldStyles.textarea} />
            </label>
            <div style={styles.formActions}>
              <button type="button" onClick={() => setEditing(false)} style={styles.cancelButton} disabled={saving}>Annulla</button>
              <button type="submit" style={saving ? styles.saveButtonDisabled : styles.saveButton} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <h4 style={styles.eventTitle}>{displayEventName(eventInfo.eventName)}</h4>

            <div style={styles.infoList}>
              {rows.map((r) => (
                <InfoRow key={r.label} label={r.label} value={r.value ? (r.render || r.value) : 'Non indicato'} muted={!r.value} />
              ))}
            </div>

            <div style={styles.notesSection}>
              <span style={styles.sectionLabel}>Note</span>
              <div style={styles.notesText}>{eventInfo.notes && eventInfo.notes.trim() ? eventInfo.notes : 'Nessuna nota'}</div>
            </div>

            {canEdit && (
              <div style={styles.editRow}>
                <button type="button" onClick={startEditing} style={styles.editButton}>Modifica</button>
              </div>
            )}
            <DeleteEventButton eventKey={eventInfo.eventKey} eventName={eventInfo.eventName} backTarget={backTarget} />
          </>
        )}
      </div>
    </Page>
  );
}

function InfoRow({ label, value, muted }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={muted ? styles.infoValueMuted : styles.infoValue}>{value}</span>
    </div>
  );
}

const styles = {
  content: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius,
    boxShadow: theme.shadows.main,
    color: theme.colors.text,
    width: '100%',
    maxWidth: '700px',
    padding: '10px 20px 25px 20px',
    boxSizing: 'border-box',
    marginBottom: '25px',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backArrow: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: theme.colors.brand,
    padding: 0,
  },
  typeBadge: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '2px 10px',
  },
  eventTitle: {
    textAlign: 'center',
    fontSize: '20px',
    margin: '15px 0 20px 0',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '10px 12px',
    backgroundColor: theme.colors.background,
    borderRadius: '4px',
  },
  infoLabel: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: '15px',
  },
  infoValueMuted: {
    fontSize: '15px',
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  whatsapp: {
    color: '#128C7E',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  notesSection: {
    borderTop: `1px solid ${theme.colors.border}`,
    paddingTop: '15px',
  },
  sectionLabel: {
    display: 'block',
    fontSize: '12px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  notesText: {
    whiteSpace: 'pre-wrap',
    fontSize: '15px',
    lineHeight: 1.4,
  },
  editRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  },
  editButton: {
    padding: '8px 20px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  cancelButton: {
    padding: '10px 16px',
    backgroundColor: theme.colors.white,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  saveButtonDisabled: {
    padding: '10px 20px',
    backgroundColor: theme.colors.border,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontSize: '14px',
  },
};

export default PrivateEvent;
